import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Plus, Bell, Inbox, Briefcase, Users, BarChart3, Settings, Home,
  ChevronRight, X, Star, FileText, MessageSquare, Calendar as CalIcon, Award,
  ArrowRight, Sparkles, Clock, ChevronDown, Filter, Layers, Eye, ShieldCheck,
  ThumbsUp, ThumbsDown, ChevronsRight, MoreHorizontal, Zap,
} from 'lucide-react';
import { logger } from './utils/logger';
import RecruiterCandidateActions from './shell/RecruiterCandidateActions';
import RequisitionAIOverviews from './shell/RequisitionAIOverviews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Design tokens — synced with /app/design_guidelines.json
// ---------------------------------------------------------------------------
const RAG = {
  red: { dot: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  amber: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  blue: { dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  grey: { dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
};

const STAGES = [
  { id: 'applied', label: 'New' },
  { id: 'screening', label: 'Screening' },
  { id: 'interview', label: 'Interview' },
  { id: 'decision', label: 'Decision' },
  { id: 'offer', label: 'Offer' },
  { id: 'hired', label: 'Hired' },
  { id: 'rejected', label: 'Rejected' },
];

// ===========================================================================
export default function RecruiterHQ() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('today');
  const [quickReviewAppId, setQuickReviewAppId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd+K opens search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setQuickReviewAppId(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex" style={{ fontFamily: 'IBM Plex Sans, system-ui, sans-serif' }} data-testid="recruiter-hq">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 flex flex-col shrink-0" data-testid="recruiter-sidebar">
        <div className="p-5 border-b border-slate-200">
          <Link to="/" className="font-semibold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="w-6 h-6 rounded bg-slate-900 text-white flex items-center justify-center text-xs">V</span>
            Vault Hire
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {[
            ['today', Home, 'Today'],
            ['jobs', Briefcase, 'My Jobs'],
            ['pipeline', Layers, 'Pipeline'],
            ['sourcing', Users, 'Sourcing'],
            ['decisions', Award, 'Decisions'],
            ['calendar', CalIcon, 'Calendar'],
          ].map(([id, Icon, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} data-testid={`tab-${id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition ${
                activeTab === id ? 'bg-slate-900 text-white font-medium' : 'text-slate-600 hover:bg-slate-100'
              }`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <button onClick={() => navigate('/employer/dashboard')} className="w-full text-xs text-slate-500 hover:text-slate-900 text-left px-3 py-1.5">← Employer dashboard</button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3" data-testid="recruiter-header">
          <div className="flex-1 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              onFocus={() => setSearchOpen(true)}
              placeholder="Search candidates, jobs, requisitions…  ⌘K"
              data-testid="global-search-input"
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white"
            />
          </div>
          <QuickCreateMenu />
          <RecruiterCandidateActions />
          <button className="p-2 hover:bg-slate-100 rounded-md text-slate-600" data-testid="notifications-btn"><Bell className="w-4 h-4" /></button>
        </header>

        {/* Tab content */}
        <main className="flex-1 overflow-auto p-6" data-testid="recruiter-main">
          {activeTab === 'today' && <TodayView onOpenQuickReview={setQuickReviewAppId} onJumpToTab={setActiveTab} />}
          {activeTab === 'jobs' && <MyJobsView onOpenQuickReview={setQuickReviewAppId} />}
          {activeTab === 'pipeline' && <PipelineView onOpenQuickReview={setQuickReviewAppId} />}
          {activeTab === 'sourcing' && <ComingSoon name="Sourcing & CRM" desc="Referrals inbox, talent pool searches, saved candidate lists, outreach sequences." />}
          {activeTab === 'decisions' && <ComingSoon name="Decisions" desc="Offer management, compliance pack requests, assessment triggers, onboarding hand-off." />}
          {activeTab === 'calendar' && <ComingSoon name="Calendar" desc="Recruiter availability, screening calls, interview slots, hiring-manager bookings." />}
        </main>
      </div>

      {/* Quick Review Sheet (right-side slide-over) */}
      {quickReviewAppId && <QuickReviewSheet applicationId={quickReviewAppId} onClose={() => setQuickReviewAppId(null)} />}
      {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function TodayView({ onOpenQuickReview, onJumpToTab }) {
  const [nba, setNba] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruiter/nba?limit=8`);
      setNba(data.items || []);
      setMeta(data);
    } catch (err) { logger.error('nba load', err); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAction = (item, action) => {
    if (action.type === 'quick_review' && action.application_id) {
      onOpenQuickReview(action.application_id);
    } else if (action.type === 'open_pipeline' || action.type === 'open_new_inbox') {
      onJumpToTab('pipeline');
    } else {
      window.alert(`Action "${action.label}" will deep-link in Phase 2 — wired through to ${action.type}`);
    }
  };

  return (
    <div data-testid="today-view">
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">Today</p>
        <h1 className="text-3xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Next best actions
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Prioritised by {meta?.engine === 'rules_v1' ? 'deterministic rules' : 'AI assistance'}. Top {nba.length} of {meta?.total || 0}.
        </p>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[1, 2, 3].map((i) => <div key={i} className="w-72 h-44 bg-white border border-slate-200 rounded-lg animate-pulse shrink-0" />)}
        </div>
      ) : nba.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1" role="region" aria-label="Next Best Actions" data-testid="nba-strip">
          {nba.map((item) => <NBACard key={item.id} item={item} onAction={(a) => handleAction(item, a)} />)}
        </div>
      )}
    </div>
  );
}

function NBACard({ item, onAction }) {
  const rag = RAG[item.rag] || RAG.grey;
  return (
    <div
      data-testid={`nba-card-${item.action_id}`}
      className="w-72 shrink-0 bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-default flex flex-col"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${rag.dot}`} />
        <span className={`text-xs font-semibold tracking-widest uppercase ${rag.text}`}>{item.deadline_label}</span>
      </div>
      <h3 className="text-lg tracking-tight font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {item.verb}
      </h3>
      <p className="text-sm text-slate-600 mb-1">{item.object_name}</p>
      {item.object_subtitle && <p className="text-xs text-slate-400 mb-4">{item.object_subtitle}</p>}
      <div className="mt-auto flex flex-col gap-1.5">
        {(item.quick_actions || []).map((a, idx) => (
          <button
            key={idx}
            onClick={() => onAction(a)}
            data-testid={`nba-action-${item.action_id}-${idx}`}
            className={`w-full text-sm py-1.5 rounded-md transition ${
              idx === 0 ? 'bg-slate-900 hover:bg-slate-800 text-white font-medium' : 'border border-slate-200 hover:bg-slate-50 text-slate-700'
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="nba-empty">
      <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-3" />
      <h3 className="text-lg font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Your pipeline is clear.</h3>
      <p className="text-sm text-slate-500 mb-4">No urgent actions. Time to source candidates or open a new role.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
function MyJobsView({ onOpenQuickReview }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/recruiter/jobs`).then((r) => setJobs(r.data || []))
      .catch((e) => logger.error('jobs', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-slate-400">Loading jobs…</p>;
  return (
    <div data-testid="my-jobs-view">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">My Jobs</p>
          <h1 className="text-3xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{jobs.length} active role{jobs.length === 1 ? '' : 's'}</h1>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="jobs-empty">
          <Briefcase className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <h3 className="font-medium text-slate-900 mb-1">No jobs assigned yet</h3>
          <p className="text-sm text-slate-500">Open a requisition to start your pipeline.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="my-jobs-list">
          {jobs.map((j) => (
            <li key={j.id} data-testid={`my-job-${j.id}`} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition">
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{j.title || 'Untitled role'}</h3>
                    {!j.briefing_complete && <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Briefing incomplete</span>}
                    {j.published && <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">Published</span>}
                  </div>
                  <p className="text-xs text-slate-500">{j.pipeline_total} candidate{j.pipeline_total === 1 ? '' : 's'} in pipeline · created {j.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  {Object.entries(j.pipeline_by_stage || {}).slice(0, 4).map(([stage, n]) => (
                    <span key={stage} className="flex flex-col items-center">
                      <span className="font-semibold text-slate-900 text-sm">{n}</span>
                      <span className="capitalize">{stage}</span>
                    </span>
                  ))}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function PipelineView({ onOpenQuickReview }) {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/recruiter/jobs`).then((r) => {
      setJobs(r.data || []);
      if (r.data?.[0]) setSelectedJob(r.data[0].id);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedJob) return;
    axios.get(`${API}/recruiter/pipeline/${selectedJob}`).then((r) => setBoard(r.data || {}));
  }, [selectedJob]);

  const moveStage = async (appId, newStage) => {
    try {
      await axios.post(`${API}/recruiter/pipeline/move`, { application_id: appId, new_stage: newStage });
      const r = await axios.get(`${API}/recruiter/pipeline/${selectedJob}`);
      setBoard(r.data || {});
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to move');
    }
  };

  if (loading) return <p className="text-sm text-slate-400">Loading pipeline…</p>;
  if (jobs.length === 0) return <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="pipeline-empty"><Layers className="w-8 h-8 mx-auto text-slate-300 mb-3" /><p className="text-sm text-slate-500">No active roles to show a pipeline for.</p></div>;

  return (
    <div data-testid="pipeline-view">
      <div className="flex items-baseline justify-between mb-5 gap-4">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">Pipeline</p>
          <select value={selectedJob || ''} onChange={(e) => setSelectedJob(e.target.value)} data-testid="pipeline-job-selector"
            className="text-2xl tracking-tight font-semibold text-slate-900 bg-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded px-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
        </div>
      </div>

      <RequisitionAIOverviews requisitionId={selectedJob} />

      <div className="flex gap-4 overflow-x-auto pb-4 items-start" data-testid="kanban">
        {STAGES.map((stage) => {
          const cards = board[stage.id] || [];
          return (
            <div key={stage.id} data-testid={`kanban-column-${stage.id}`} className="w-80 shrink-0 flex flex-col gap-3 bg-slate-50 rounded-lg p-3 border border-slate-100">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-xs font-semibold tracking-widest uppercase text-slate-600">{stage.label}</h4>
                <span className="text-xs text-slate-500">{cards.length}</span>
              </div>
              {cards.length === 0 ? (
                <div className="text-xs text-slate-400 italic px-3 py-6 text-center border border-dashed border-slate-200 rounded-md">No candidates</div>
              ) : (
                cards.map((c) => (
                  <div key={c.id} data-testid={`pipeline-card-${c.id}`} className="bg-white p-3 rounded-md border border-slate-200 shadow-sm hover:border-slate-300 transition group">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 shrink-0">
                        {(c.candidate_name || '?').slice(0, 1)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{c.candidate_name}</p>
                        <p className="text-xs text-slate-500 truncate">{c.current_title || 'No title'}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1">
                      <button
                        onClick={() => onOpenQuickReview(c.id)}
                        data-testid={`quick-review-${c.id}`}
                        className="flex-1 text-xs px-2 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded inline-flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> Quick review
                      </button>
                      <select
                        value={c.current_stage}
                        onChange={(e) => moveStage(c.id, e.target.value)}
                        data-testid={`move-stage-${c.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs px-1.5 py-1 border border-slate-200 rounded hover:border-slate-300"
                        aria-label="Move stage"
                      >
                        {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function QuickReviewSheet({ applicationId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/recruiter/quick-review/${applicationId}`)
      .then((r) => setData(r.data))
      .catch((e) => logger.error('quick review', e))
      .finally(() => setLoading(false));
  }, [applicationId]);

  const moveAndClose = async (newStage) => {
    try {
      await axios.post(`${API}/recruiter/pipeline/move`, { application_id: applicationId, new_stage: newStage });
      onClose();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 h-screen w-full sm:max-w-3xl bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col"
        role="dialog"
        aria-label="Quick candidate review"
        data-testid="quick-review-sheet"
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            {loading || !data ? <div className="h-6 w-48 bg-slate-100 rounded animate-pulse" /> : (
              <>
                <h2 className="text-xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="qr-candidate-name">
                  {data.candidate.full_name || 'Unnamed candidate'}
                </h2>
                <p className="text-sm text-slate-500">Applied for {data.requisition?.title || 'role'}</p>
              </>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md" aria-label="Close" data-testid="qr-close-btn">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-6 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-5">
            <section data-testid="qr-summary">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Summary</h3>
              {loading ? <div className="space-y-2"><div className="h-3 bg-slate-100 rounded w-full animate-pulse" /><div className="h-3 bg-slate-100 rounded w-3/4 animate-pulse" /></div> : (
                <p className="text-sm text-slate-700 leading-relaxed">
                  {data?.candidate?.summary || data?.candidate?.ai_profile?.slice(0, 600) || 'No summary on file.'}
                </p>
              )}
            </section>
            {data?.candidate?.skills?.length > 0 && (
              <section data-testid="qr-skills">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Skills</h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.candidate.skills.slice(0, 12).map((s, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{s}</span>
                  ))}
                </div>
              </section>
            )}
            {data?.scorecards?.length > 0 && (
              <section data-testid="qr-scorecards">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Scorecards</h3>
                <ul className="space-y-1.5">
                  {data.scorecards.map((s) => (
                    <li key={s.id} className="text-sm text-slate-700 flex items-center justify-between bg-slate-50 rounded px-3 py-1.5">
                      <span>{s.interviewer_name || 'Reviewer'}</span>
                      <span className="font-semibold">{s.overall_score}/5</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <div className="space-y-4">
            {!loading && data && (
              <section data-testid="qr-actions">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Actions</h3>
                <RecruiterCandidateActions
                  candidateEmail={data.candidate?.email}
                  candidateUserId={data.candidate?.user_id || data.candidate?.id}
                  candidateName={data.candidate?.full_name}
                  requisitionId={data.requisition?.id}
                  compact
                />
              </section>
            )}
            <section className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="qr-pack">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Pack readiness</h3>
              {loading || !data ? <div className="h-8 bg-slate-100 rounded animate-pulse" /> : (
                <>
                  <p className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {data.pack?.signals_on || 0} / {data.pack?.manifest_total || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Signal-only — no documents leaked.</p>
                </>
              )}
            </section>
            <section className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="qr-experience">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Experience</h3>
              <p className="text-sm text-slate-700">{data?.candidate?.current_title || 'No current title'}</p>
              <p className="text-xs text-slate-500">{data?.candidate?.current_employer || ''}</p>
              {data?.candidate?.years_experience && <p className="text-xs text-slate-500 mt-2">{data.candidate.years_experience} years experience</p>}
            </section>
            {data?.avg_score && (
              <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-700 mb-1">Avg score</h3>
                <p className="text-2xl font-semibold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.avg_score}/5</p>
              </section>
            )}
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-slate-200 flex items-center gap-2 bg-white sticky bottom-0">
          <button onClick={() => moveAndClose('interview')} data-testid="qr-move-interview" className="flex-1 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm rounded-md font-medium inline-flex items-center justify-center gap-1">
            <ChevronsRight className="w-4 h-4" /> Move to Interview
          </button>
          <button onClick={() => moveAndClose('rejected')} data-testid="qr-reject" className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm rounded-md inline-flex items-center gap-1">
            <ThumbsDown className="w-4 h-4" /> Reject
          </button>
          <button onClick={() => moveAndClose('decision')} data-testid="qr-advance" className="px-4 py-2 border border-emerald-200 hover:bg-emerald-50 text-emerald-700 text-sm rounded-md inline-flex items-center gap-1">
            <ThumbsUp className="w-4 h-4" /> Advance to Decision
          </button>
        </footer>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
function QuickCreateMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = [
    { label: 'New requisition', path: '/cockpit', testid: 'qc-new-req' },
    { label: 'New candidate', path: '/cockpit', testid: 'qc-new-candidate' },
    { label: 'Compose offer', path: '/cockpit', testid: 'qc-new-offer' },
    { label: 'Trigger assessment', path: '/cockpit', testid: 'qc-new-assessment' },
  ];
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="quick-create-btn"
        className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm inline-flex items-center gap-1 shadow-sm">
        <Plus className="w-4 h-4" /> New
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-40 py-1" data-testid="quick-create-menu">
            {items.map((it) => (
              <button key={it.testid} data-testid={it.testid} onClick={() => { setOpen(false); navigate(it.path); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700">
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SearchPalette({ onClose }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-start justify-center pt-24" onClick={onClose} data-testid="search-palette">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b border-slate-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input autoFocus placeholder="Type to search — Phase 2 will index candidates, jobs, requisitions" className="flex-1 text-sm focus:outline-none" data-testid="search-palette-input" />
          <kbd className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">esc</kbd>
        </div>
        <div className="p-8 text-center text-sm text-slate-400">Search index coming in Phase 2.</div>
      </div>
    </div>
  );
}

function ComingSoon({ name, desc }) {
  return (
    <div data-testid={`coming-soon-${name.toLowerCase().replace(/\W+/g, '-')}`} className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
      <Zap className="w-8 h-8 mx-auto text-slate-300 mb-3" />
      <h3 className="text-lg font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{name}</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">{desc}</p>
      <p className="text-xs text-slate-400 mt-4">Wiring this surface in the next phase.</p>
    </div>
  );
}
